import clsx from "clsx";
import { ChevronDown, Info } from "lucide-react";
import { useLocalStorage } from "../lib/hooks";
import { FILL_BANDS, bandRange, bandTone } from "../lib/history";

/**
 * A short box at the top of a page that says where its figures come from and how to read
 * them, for someone opening the page for the first time. Collapsed by default and
 * remembered per browser, so a duty manager who has read it once is not shown it every
 * shift; the bullets are the page's own, passed in as list items.
 */
export function HowToRead({ id, children }: { id: string; children: React.ReactNode }) {
  const [open, setOpen] = useLocalStorage(`yb.howto.${id}`, false);
  return (
    <div className={clsx("mb-4 rounded-xl border border-line bg-panel-2/40", !open && "print:hidden")}>
      <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12.5px] font-semibold text-ink-2 hover:text-ink" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Info className="size-4 shrink-0 text-ink-3" />
        How to read this page
        <ChevronDown className={clsx("ml-auto size-4 shrink-0 text-ink-3 transition-transform", open && "rotate-180")} />
      </button>
      {open && <ul className="flex flex-col gap-1.5 px-4 pt-0.5 pb-3 pl-9 text-[12.5px] text-ink-2 [&>li]:list-disc">{children}</ul>}
    </div>
  );
}

/**
 * The fill scale as five coloured chips with their edges, from the one scale every fill
 * on the History and Forecast pages is coloured on, so the box cannot say 70% where the
 * bar says 69.
 */
export function FillScaleChips() {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 align-middle">
      {FILL_BANDS.map((b) => {
        const tone = bandTone(b);
        return (
          // "Over" is its own edge, so that chip says "over 100%" once rather than "over over 100%".
          <span key={b} className={clsx("inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold text-panel", tone.bar)} data-band={b}>
            {b === "over" ? "over 100%" : `${tone.label} ${bandRange(b)}`}
          </span>
        );
      })}
    </span>
  );
}
